/**
 * FE-3/FE-6 — role→SPA routing. The backend computes `reachableApps` (BE-3, from RBAC roles); this
 * maps each app code to WHERE that SPA lives and derives the post-login destination. Kept as ONE
 * registry so the app codes stay in lockstep with the backend's AppSurfaces map. The login app is NOT
 * one of these personas — it authenticates and redirects OUT to one of them.
 *
 * SURF-GATE — WHY THIS LIVES IN `@zercode/auth` RATHER THAN `@zercode/auth-ui`:
 * it used to sit in auth-ui, which only `apps/login` depends on, so only the LOGIN ORIGIN could act on
 * `reachableApps`. That closed the front door and left every side door open: a student holding a live
 * session cookie (`Domain=zercode.uk`, so it spans the persona subdomains) could type the admin host
 * and get the admin shell — `ProtectedRoute` checked authentication and nothing else. The gate now
 * needs `appUrl` INSIDE each SPA, so the registry moved down to the package every SPA already has.
 * `@zercode/auth-ui` re-exports the whole surface, so `apps/login` imports exactly what it did before.
 */

export type AppCode = 'admin' | 'teacher' | 'student' | 'super-admin';

interface AppEntry {
  /** i18n key for the persona label shown in the picker (FE-4) */
  labelId: string;
  /** English fallback for the label (pilot English-only) [STD-I18N] */
  labelDefault: string;
  /** i18n key for the one-line persona description in the picker */
  descId: string;
  /** English fallback for the description */
  descDefault: string;
  /** post-login landing path within that SPA */
  /** production/staging subdomain label (leftmost host label), e.g. superadmin.zercode.uk */
  prodSubdomain: string;
  /** local dev port (root README port map) */
  devPort: number;
}

const APPS: Record<AppCode, AppEntry> = {
  admin: {
    labelId: 'app-admin',
    labelDefault: 'Administration',
    descId: 'app-admin-desc',
    descDefault: 'Manage your institution',
    prodSubdomain: 'admin',
    devPort: 3001
  },
  teacher: {
    labelId: 'app-teacher',
    labelDefault: 'Teaching',
    descId: 'app-teacher-desc',
    descDefault: 'Your classes, grading and attendance',
    prodSubdomain: 'teacher',
    devPort: 3004
  },
  student: {
    labelId: 'app-student',
    labelDefault: 'Learning',
    descId: 'app-student-desc',
    descDefault: 'Your courses, exams and progress',
    prodSubdomain: 'student',
    devPort: 3003
  },
  'super-admin': {
    labelId: 'app-super-admin',
    labelDefault: 'Platform Admin',
    descId: 'app-super-admin-desc',
    descDefault: 'Platform-wide administration',
    prodSubdomain: 'superadmin',
    devPort: 3002
  }
};

export const APP_CODES = Object.keys(APPS) as AppCode[];

export function isAppCode(value: string): value is AppCode {
  return Object.prototype.hasOwnProperty.call(APPS, value);
}

const LOCAL_HOSTS = new Set(['localhost', '127.0.0.1', '0.0.0.0', '[::1]']);

/** Minimal shape of `window.location` this module needs (injectable for tests). */
export interface LocationLike {
  protocol: string; // includes trailing ':', e.g. 'https:'
  hostname: string;
}

/**
 * APP-URL-ENV — the CONFIGURED home of each SPA, when the deployment states it outright.
 *
 * The derivation in `appUrl` below guesses a host by string-surgery on the login origin. That guess
 * is right only for the two topologies it was written for (bare apex, or `login.<base>`); it is
 * wrong the moment the login app is served from anything else — a `www.` host yields
 * `admin.www.teeneagle.co.uk` — and it can only ever produce the ONE label baked into
 * `prodSubdomain`, which is how the registry came to emit `superadmin.` while the login app's
 * `VITE_CONSOLE_ORIGINS` declares `super-admin.`. So the deployment now names the hosts.
 *
 * ⚠ VALUES ARE INJECTED, NOT READ FROM `import.meta.env` HERE. `@zercode/auth` is `tsc`-built for
 * publishing with `"types": []` — it has no Vite ambient types and must not grow a build-time env
 * dependency. Each app reads its own env and calls `configureAppUrls` (same shape as
 * `resolveTenant({ devFallback })` in `@zercode/tenancy`).
 *
 * ⚠ THIS FEEDS THE OPEN-REDIRECT ALLOWLIST TOO. `isAllowedReturnUrl` / `appCodeForUrl` resolve
 * through `appOrigin` → `appUrl`, so configuring a host here both redirects TO it and accepts a
 * `?return=` FROM it — one source, so the login origin's guard and each SPA's SURF-GATE bounce
 * cannot disagree. The corollary is that every app must be built with the SAME values: if the login
 * app knows `student.teeneagle.co.uk` and the student SPA does not, login accepts a deep link that
 * the student SPA then bounces. `pnpm env:app-urls` is the gate for exactly that.
 */
const configuredUrls: Partial<Record<AppCode, string>> = {};

/**
 * Name where each SPA lives, overriding the host derivation. Call it ONCE at app bootstrap, from the
 * module that already owns the env seam (`src/lib/zercode.ts`), so it runs before any route renders.
 *
 * - An absent, empty or unparseable value leaves that app on the derivation — a typo in one env file
 *   degrades to today's behaviour instead of throwing during module init and taking the SPA down.
 * - A value is reduced to its ORIGIN: any path is dropped (LAND-CFG — the landing page is a tenant
 *   setting resolved by the destination's own `<LandingRedirect />`; baking a path here would make
 *   the login flow skip that setting entirely, which is the bug the `homePath` note below records).
 * - `configureAppUrls({})` clears everything — how tests reset between cases.
 */
export function configureAppUrls(urls: Partial<Record<AppCode, string | undefined | null>>): void {
  for (const app of APP_CODES) {
    delete configuredUrls[app];
    const raw = urls[app]?.trim();
    if (!raw) continue;
    try {
      configuredUrls[app] = `${new URL(raw).origin}/`;
    } catch {
      // malformed value → leave this app on the derivation (see above)
    }
  }
}

/**
 * Absolute URL (including the app's home path) of an SPA: the configured host when the deployment
 * named one (`configureAppUrls`), else derived from the current (login) location.
 * The derivation is the FALLBACK, kept for local dev (where no env var names a localhost port) and
 * for any app a deployment has not named yet. The login app is a NEUTRAL origin (apex `zercode.uk`,
 * or `login.<base>`) — never a persona subdomain — so the target is built as `<appSubdomain>.<base>`,
 * where `<base>` is the login host minus a leading `login.` service label:
 * - apex `zercode.uk`        → `admin.zercode.uk`
 * - `login.zercode.uk`       → `admin.zercode.uk`
 * - `login.demo.zercode.uk`  → `admin.demo.zercode.uk`
 * - dev `localhost`          → same host, the app's dev port (cookies are host-only across ports).
 * (NB: blindly swapping the leftmost label breaks the apex — `zercode.uk` → `admin.uk`.) The
 * `Domain=zercode.uk` refresh cookie spans these, so the target SPA silently restores the session.
 * ⚠ HEDEF UYGULAMANIN KÖKÜ (`/`) — sabit bir açılış yolu DEĞİL (LAND-CFG).
 *
 * Burada `homePath` gibi bir sabit tutmak, açılış sayfasını ÜÇÜNCÜ bir yerde tanımlamak olurdu:
 * kiracı Ayarlar → Açılış Sayfaları'ndan seçim yapsa bile giriş akışı kullanıcıyı doğrudan eski
 * sabit yola atardı ve ayar hiç yürürlüğe girmezdi (canlıda böyle bulundu). Kökte her SPA'nın
 * kendi <LandingRedirect />'i çalışır; o da ayarı KÜRATÖRLÜ listeye ve kullanıcının
 * erişilebilirliğine göre çözer — yani ayarlanan sayfayı açamayan kullanıcı 403 değil kendi
 * varsayılanını görür. Derin bağlantı korunur: geçerli bir `returnUrl` bu yoldan ÖNCE kazanır.
 */
export function appUrl(app: AppCode, loc: LocationLike = window.location): string {
  const entry = APPS[app];
  // APP-URL-ENV: the deployment's own answer wins over the guess — including on localhost, so a dev
  // pointing at a staging console gets it rather than a port on their machine.
  const configured = configuredUrls[app];
  if (configured) {
    return configured;
  }
  if (LOCAL_HOSTS.has(loc.hostname)) {
    return `${loc.protocol}//${loc.hostname}:${entry.devPort}/`;
  }
  const base = loc.hostname.startsWith('login.') ? loc.hostname.slice('login.'.length) : loc.hostname;
  return `${loc.protocol}//${entry.prodSubdomain}.${base}/`;
}

/** The origin (scheme://host[:port]) an app is served from, relative to the current location. */
export function appOrigin(app: AppCode, loc: LocationLike = window.location): string {
  return new URL(appUrl(app, loc)).origin;
}

/**
 * Open-redirect guard: a return URL is accepted ONLY if it is absolute and its origin exactly
 * matches one of the known SPA origins. Anything else (attacker.com, javascript:, relative junk)
 * is rejected — the login app never redirects to an unvetted URL [security].
 */
export function isAllowedReturnUrl(returnUrl: string, loc: LocationLike = window.location): boolean {
  let target: URL;
  try {
    target = new URL(returnUrl);
  } catch {
    return false;
  }
  return APP_CODES.some((app) => appOrigin(app, loc) === target.origin);
}

/** Which app (if any) a return URL belongs to — matched by origin. */
export function appCodeForUrl(returnUrl: string, loc: LocationLike = window.location): AppCode | null {
  let target: URL;
  try {
    target = new URL(returnUrl);
  } catch {
    return null;
  }
  return APP_CODES.find((app) => appOrigin(app, loc) === target.origin) ?? null;
}

export interface PersonaOption {
  app: AppCode;
  labelId: string;
  labelDefault: string;
  descId: string;
  descDefault: string;
  url: string;
}

function personaOption(app: AppCode, loc?: LocationLike): PersonaOption {
  return {
    app,
    labelId: APPS[app].labelId,
    labelDefault: APPS[app].labelDefault,
    descId: APPS[app].descId,
    descDefault: APPS[app].descDefault,
    url: appUrl(app, loc)
  };
}

/**
 * FE-6 — post-login routing FROM the dedicated login origin (which is never a persona itself, so it
 * always redirects OUT):
 * - `none`     → 0 reachable apps (actionable error).
 * - `redirect` → go to a single URL: the validated `returnUrl` when the user can reach that app
 *                (deep-link preserved), else the server-chosen `defaultApp`, else the sole
 *                reachable app's home.
 * - `pick`     → ≥2 reachable apps, no usable return target AND no usable `defaultApp` → persona
 *                picker (FE-4).
 *
 * RSU — why `defaultApp` exists: roles ACCUMULATE on the backend. Approving a Partner Employee
 * application grants `partner_employee` (surface `teacher`) WITHOUT removing `student`, so the
 * ordinary signup→apply→approved path leaves the user reaching BOTH `student` and `teacher`. That is
 * the common case, not an edge one, and stopping every such login at a picker is wrong: the server
 * already knows which surface is the right landing spot (`AppSurfaces.PrimaryOf`, precedence
 * super-admin > admin > teacher > student). `reachableApps` stays plural so a genuinely multi-role
 * user can still SWITCH — the picker remains for that, it just is not the post-login gate any more.
 *
 * `defaultApp` is only ever honored when it is one of `distinct`, so a stale or unknown value cannot
 * redirect a user into an app they cannot reach. Omitting it (older backend) keeps the old behaviour
 * exactly — the picker.
 */
export type AuthOriginRoute = { kind: 'none' } | { kind: 'redirect'; url: string } | { kind: 'pick'; options: PersonaOption[] };

export function resolveAuthOriginRoute(
  reachableApps: readonly string[],
  returnUrl?: string,
  loc?: LocationLike,
  defaultApp?: string | null
): AuthOriginRoute {
  const distinct = [...new Set(reachableApps.filter(isAppCode))];

  if (distinct.length === 0) {
    return { kind: 'none' };
  }

  // Honor a valid return deep-link when it points at an app the user can actually reach.
  // This still outranks defaultApp: an explicit deep-link is a stronger signal of intent than a
  // precedence rule (the user clicked a link INTO that app).
  if (returnUrl && isAllowedReturnUrl(returnUrl, loc)) {
    const app = appCodeForUrl(returnUrl, loc);
    if (app && distinct.includes(app)) {
      return { kind: 'redirect', url: returnUrl };
    }
  }

  if (distinct.length === 1) {
    return { kind: 'redirect', url: appUrl(distinct[0]!, loc) };
  }

  // The server's landing choice — only if the user can actually reach it (see the note above).
  if (defaultApp && isAppCode(defaultApp) && distinct.includes(defaultApp)) {
    return { kind: 'redirect', url: appUrl(defaultApp, loc) };
  }

  return { kind: 'pick', options: distinct.map((app) => personaOption(app, loc)) };
}
